/**
 * DeathOverlay - Shows when user's creature dies
 * Displays survival stats and option to respawn
 */

import { useEffect, useState, useRef } from 'react';
import { useSimulationStore } from '../stores/simulationStore';
import './ui/ui.css';

interface DeathOverlayProps {
  onSpawn: () => void;
}

interface DeathStats {
  survivalTime: number; // in seconds
}

export function DeathOverlay({ onSpawn }: DeathOverlayProps) {
  const myCreatureAlive = useSimulationStore((state) => state.myCreatureAlive);
  const simulationState = useSimulationStore((state) => state.simulationState);

  const [showOverlay, setShowOverlay] = useState(false);
  const [deathStats, setDeathStats] = useState<DeathStats | null>(null);
  const wasAliveRef = useRef(false);
  const spawnTimeRef = useRef<number | null>(null);

  // Track spawn time when creature becomes alive
  useEffect(() => {
    if (myCreatureAlive && !wasAliveRef.current) {
      spawnTimeRef.current = Date.now();
    }
  }, [myCreatureAlive]);

  // Detect death transition
  useEffect(() => {
    if (wasAliveRef.current && !myCreatureAlive) {
      // Creature just died - capture stats
      const survivalTime = spawnTimeRef.current
        ? Math.round((Date.now() - spawnTimeRef.current) / 1000)
        : 0;

      setDeathStats({ survivalTime });
      setShowOverlay(true);
      spawnTimeRef.current = null;
    }
    wasAliveRef.current = myCreatureAlive;
  }, [myCreatureAlive]);

  // Hide overlay when creature becomes alive again
  useEffect(() => {
    if (myCreatureAlive && showOverlay) {
      setShowOverlay(false);
      setDeathStats(null);
    }
  }, [myCreatureAlive, showOverlay]);

  const formatTime = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds} segundos`;
    }
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes < 60) {
      return secs > 0 ? `${minutes}m ${secs}s` : `${minutes} minutos`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const handleSpawn = () => {
    setShowOverlay(false);
    onSpawn();
  };

  const handleDismiss = () => {
    setShowOverlay(false);
  };

  if (!showOverlay) return null;

  return (
    <div className="death-overlay" role="dialog" aria-labelledby="death-title">
      <div className="death-dialog">
        <div className="death-icon" aria-hidden="true">
          <span className="death-skull">💀</span>
        </div>

        <h2 id="death-title" className="death-title">Tu criatura ha muerto</h2>

        {deathStats && deathStats.survivalTime > 0 && (
          <div className="death-stats">
            <div className="death-stat">
              <span className="stat-label">Tiempo de supervivencia</span>
              <span className="stat-value">{formatTime(deathStats.survivalTime)}</span>
            </div>
          </div>
        )}

        <div className="death-actions">
          <button
            className="death-btn primary"
            onClick={handleSpawn}
            disabled={simulationState === 'stopped'}
          >
            Crear Nueva Criatura
          </button>
          <button className="death-btn secondary" onClick={handleDismiss}>
            Cerrar
          </button>
        </div>

        {simulationState === 'stopped' && (
          <p className="death-hint">La simulacion esta detenida</p>
        )}
      </div>
    </div>
  );
}
